// OAS Stream Mapping dialog — tag each stream (grade) with its measurement instrument, which sets a
// default A/B/C/D accuracy grade you can override. The OAS-side sibling of the Product Mapping.
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, MenuItem, Box, Typography, Chip,
} from '@mui/material';
import {
  StreamMapping as SM, INSTRUMENTS, GRADES, instrumentGrade, gradeForStream, gradeColor,
} from '../utils/streamMapping';

export interface StreamRow { key: string; label: string; size: number; }

interface Props {
  open: boolean;
  onClose: () => void;
  streams: StreamRow[];               // pre-sorted by size desc
  mapping: SM;
  onChange: (m: SM) => void;
  onSave: () => void;
  saving?: boolean;
  unit?: string;
}

const StreamMapping: React.FC<Props> = ({ open, onClose, streams, mapping, onChange, onSave, saving, unit }) => {
  const setTag = (key: string, patch: { instrument?: string; grade?: string }) => {
    const cur = mapping.assignments[key] || { instrument: '', grade: '' };
    const next = { ...cur, ...patch };
    // Setting the instrument resets the grade to that instrument's default (still overridable after).
    if (patch.instrument !== undefined) next.grade = instrumentGrade(patch.instrument) || '';
    onChange({ ...mapping, assignments: { ...mapping.assignments, [key]: next } });
  };

  const fmt = (n: number) => (n ? Math.round(n).toLocaleString() : '0');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Stream Mapping — accuracy by instrument</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Tag each stream with how it&apos;s measured. The instrument sets the accuracy grade
          (Coriolis = A, PD/Turbine/PV = B, calculated = C, estimated = D). Streams are listed largest-first.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Stream</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Size{unit ? ` (${unit})` : ''}</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>Instrument (accuracy)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {streams.map((s) => {
                const tag = mapping.assignments[s.key] || { instrument: '', grade: '' };
                return (
                  <TableRow key={s.key} hover>
                    <TableCell>{s.label}</TableCell>
                    <TableCell align="right">{fmt(s.size)}</TableCell>
                    <TableCell>
                      <TextField select size="small" fullWidth value={tag.instrument || ''}
                        onChange={(e) => setTag(s.key, { instrument: e.target.value })}>
                        <MenuItem value=""><em>— untagged —</em></MenuItem>
                        {INSTRUMENTS.map((i) => <MenuItem key={i.id} value={i.id}>{i.label} ({i.grade})</MenuItem>)}
                      </TextField>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StreamMapping;
